"use client"

import React, { useRef, useEffect, useState } from "react"
import { Menu, TitleNameSlug } from "../../@types/Menu"
import Link from "next/link"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { ROUTES } from "@/config/routes"
import { createPortal } from "react-dom"

interface SimpleNavButtonProps {
  menu: Menu
  hasSubmenu?: boolean
  position?: "left" | "right"
  scrollOffset?: number
}

export default function SimpleNavButton({
  menu,
  hasSubmenu = false,
  position = "left",
  scrollOffset = 0,
}: SimpleNavButtonProps) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const popoverButtonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0, width: 0 })
  const [mounted, setMounted] = useState(false)
  const [windowWidth, setWindowWidth] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  //console.log(menu);
  useEffect(() => {
    setMounted(true)
    setWindowWidth(window.innerWidth)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    const updatePosition = () => {
      if (popoverButtonRef.current) {
        const rect = popoverButtonRef.current.getBoundingClientRect()
        setButtonPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        })
        setWindowWidth(window.innerWidth)
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [])

  const showPopover = () => {
    setIsVisible(true)
  }

  const hidePopover = () => {
    setIsVisible(false)
  }

  if (!hasSubmenu) {
    return (
      <Link
        href={menu.slug}
        className="group inline-flex items-center gap-x-1 leading-6 text-gray-900 hover:text-yellow-500 py-4"
      >
        <span className="uppercase">{menu.name}</span>
      </Link>
    )
  }

  const submenuContent = mounted ? (
    <div
      ref={popoverRef}
      className={`fixed top-[119px] transition-opacity duration-200 ${isVisible ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      style={{
        zIndex: 9999,
        left: position === "left" ? `${buttonPosition.left - scrollOffset}px` : 'auto',
        right: position === "right" ? `${windowWidth - (buttonPosition.left + buttonPosition.width) + scrollOffset}px` : 'auto',
      }}
    >
      <div className="w-full h-[5px] bg-matco"></div>
      <div className="bg-white shadow-lg min-w-[120px] max-w-[800px]">
        <div className="py-2">
          {Object.entries(menu)
            .filter(([key, value]) => Array.isArray(value))
            .flatMap(([key, value]) => value as TitleNameSlug[])
            .map((item, index) => (
              <Link
                key={index}
                target={(item.link && item.is_blank == '1') ? "_blank" : "_self"}
                href={
                  item.link ??
                  (menu.slug === "marcas"
                    ? ROUTES.brands.brand(item.slug)
                    : ROUTES.usedEquipmentsCertificate.root ===
                      `/${item.slug}` || menu.slug === item.slug
                      ? `/${item.slug}`
                      : `/${menu.slug}/${item.slug}`)
                }
                className="block py-2 px-4 text-sm text-gray-900 hover:text-black hover:bg-gray01 uppercase text-left whitespace-pre-wrap break-words"
                onClick={hidePopover}
              >
                {item.title}
              </Link>
            ))}
        </div>
      </div>
    </div>
  ) : null

  return (
    <div ref={containerRef} onMouseLeave={hidePopover}>
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <PopoverButton
              ref={popoverButtonRef}
              onClick={() => {
                hidePopover()
                window.location.href = `/${menu.slug}`
              }}
              className="group inline-flex items-center gap-x-1 leading-6 text-gray-900 py-4 whitespace-nowrap"
              onMouseEnter={showPopover}
            >
              <Link
                href={`/${menu.slug}`}
                className="uppercase text-ellipsis overflow-hidden hover:text-yellow-500"
                onClick={hidePopover}
              >
                {menu.name}
              </Link>
            </PopoverButton>
            {mounted && createPortal(submenuContent, document.body)}
          </>
        )}
      </Popover>
    </div>
  )
}
